import React from 'react';
import { Modal } from '../common/Modal';
import { ConfirmationDialog } from '../common/ConfirmationDialog';
import { FinishedGoodsFinalProductSection } from './FinishedGoodsFinalProductSection';
import { FinishedGoodsComponentsSection } from './FinishedGoodsComponentsSection';
import { FinishedGoodsSummarySection } from './FinishedGoodsSummarySection';
import {
  useFinishedGoodsProductionForm,
  type FinishedGoodsProductionModalMode,
  type FinishedGoodsProductionResult,
} from '../../hooks/inventory-hooks/useFinishedGoodsProductionForm';
import { useLanguage } from '../../features/language/useLanguage';
import { useOnboardingStore } from '../../store/onboardingStore';
import { DownloadLabelButton } from '../labels/DownloadLabelButton';
import { FinishedGoodLabelDocument } from '../labels/FinishedGoodLabelDocument';
import { finishedGoodScanUrl } from '../../utils/labelUrl';

interface FinishedGoodsProductionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: FinishedGoodsProductionResult) => void;
  mode?: FinishedGoodsProductionModalMode;
  productionId?: string;
  canWrite?: boolean;
}

export const FinishedGoodsProductionModal: React.FC<FinishedGoodsProductionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode = 'create_draft',
  productionId,
  canWrite = true,
}) => {
  const { t } = useLanguage();
  const {
    unit,
    toggleUnit,
    control,
    handleSubmit,
    register,
    formState,
    setValue,
    watch,
    fields,
    remove,
    finalQuantityInput,
    setFinalQuantityInput,
    finalProductMode,
    finalQuantityPcs,
    components,
    packSizeKg,
    totalGrams,
    totalInputKg,
    totalInputCost,
    unitCostPerPiece,
    miscCostFields,
    componentQuantityInputs,
    error,
    loading,
    submitting,
    disableSubmit,
    maxQuantity,
    handleCalculateMaxQuantity,
    handleAddComponent,
    handleAddMiscCost,
    removeMiscCost,
    handleSubmitForm,
    handleComponentSelectChange,
    handlePercentageChange,
    handleComponentWeightChange,
    handleFinalComponentWeightChange,
    handleModeSwitch,
    finishedGoodsOptions,
    availableOptionsForRow,
    draftDetail,
    isDuplicateFinalProductName,
    getRoastedBulkLabel,
  } = useFinishedGoodsProductionForm({ isOpen, onClose, onSuccess, mode, productionId });

  const { errors, isSubmitted, isDirty } = formState;
  const estimatedSellingPrice = watch('estimatedSellingPrice');
  const finalProductItemId = watch('finalProductItemId');
  const finalProductName = watch('finalProductName');
  const isCancelledView = mode === 'view' && draftDetail?.status === 'cancelled';
  const readOnly = !canWrite || mode === 'view';
  const showDraftValidation = mode === 'create_draft' && (isDirty || isSubmitted);

  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = React.useState(false);

  // Onboarding step 5 packs the practice finished good, and the tour's Back
  // button leaves this form by pressing Cancel for the user. The unsaved-changes
  // guard would answer that with a second dialog the tour has no way to dismiss —
  // its scrim refuses clicks outside the highlight — so Back would look dead
  // again. For the practice draft Back therefore just closes; it holds nothing
  // but throwaway sample data. A REAL draft opened mid-tour keeps the guard, and
  // so does every use outside the tour.
  const onboardingStep5 = useOnboardingStore(
    (s) => s.state?.status === 'in_progress' && s.state?.currentStep === 5,
  );
  const sampleProductionId = useOnboardingStore((s) => s.state?.sampleRefs?.fgProductionId);
  // create_draft has no production id yet and is only ever reached through the
  // tour's own highlighted button, so it counts as practice on step 5. The
  // draft-bound modes must match the recorded sample draft exactly.
  const isOnboardingSampleProduction =
    onboardingStep5 && (mode === 'create_draft' || (!!productionId && productionId === sampleProductionId));

  const requestClose = () => {
    if (readOnly || !isDirty || isOnboardingSampleProduction) {
      onClose();
      return;
    }
    setIsCloseConfirmOpen(true);
  };

  const confirmClose = () => {
    setIsCloseConfirmOpen(false);
    onClose();
  };

  const handleModeSwitchAndPrefill = (name: string) => {
    handleModeSwitch('new');
    setValue('finalProductName', name, { shouldValidate: true });
  };

  const selectedComponents = components.filter(c => !!c?.roastedBulkItemId && !!c?.roastType);
  const missingFinalProduct =
    finalProductMode === 'existing'
      ? !finalProductItemId
      : typeof finalProductName !== 'string' || finalProductName.trim().length === 0;
  const missingPackSize = !packSizeKg || packSizeKg <= 0;
  const missingFinalQuantity = !finalQuantityPcs || finalQuantityPcs <= 0;
  const missingComponents = selectedComponents.length === 0;
  const invalidComponents = selectedComponents.some(c => (Number(c?.percentage) || 0) <= 0 || (Number(c?.gramsPerPack) || 0) <= 0);
  const totalPercentage = selectedComponents.reduce((sum, c) => sum + (Number(c?.percentage) || 0), 0);
  const selectedTotalGrams = selectedComponents.reduce((sum, c) => sum + (Number(c?.gramsPerPack) || 0), 0);
  const targetGrams = (packSizeKg || 0) * 1000 * (finalQuantityPcs || 0);
  const invalidTotals =
    selectedComponents.length > 0 &&
    packSizeKg > 0 &&
    finalQuantityPcs > 0 &&
    (Math.abs(totalPercentage - 100) > 0.5 || Math.abs(selectedTotalGrams - targetGrams) > 0.5);

  const showDraftBlockingMessage =
    mode === 'create_draft' &&
    showDraftValidation &&
    !loading &&
    !submitting &&
    (missingFinalProduct || isDuplicateFinalProductName || missingPackSize || missingFinalQuantity || missingComponents || invalidComponents || invalidTotals);
  const title =
    mode === 'create_draft'
      ? t('finishedGoodsProduction.modal.createDraft')
      : mode === 'complete_draft'
        ? t('finishedGoodsProduction.modal.completeDraft')
        : t('finishedGoodsProduction.modal.viewDraft');

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={requestClose}
      title={title}
      headerAction={
        !readOnly && mode === 'create_draft' ? (
          <div className="flex gap-2">
            <button
              type="button"
              data-test-id="finished-goods-mode-existing"
              onClick={() => {
                handleModeSwitch('existing');
              }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
                finalProductMode === 'existing'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('finishedGoodsProduction.modal.useExisting')}
            </button>
            <button
              type="button"
              data-test-id="finished-goods-mode-new"
              onClick={() => {
                handleModeSwitch('new');
              }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
                finalProductMode === 'new'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('finishedGoodsProduction.modal.createNew')}
            </button>
          </div>
        ) : mode === 'view' && draftDetail ? (
          <DownloadLabelButton
            scanUrl={finishedGoodScanUrl(productionId as string)}
            renderDocument={(qr) => (
              <FinishedGoodLabelDocument packingCode={draftDetail.code} qrDataUrl={qr} />
            )}
            fileName={`finished-good-label-${draftDetail.code || productionId}`}
            label={t('labels.downloadLabel')}
            data-test-id="finished-good-download-label"
          />
        ) : undefined
      }
      maxWidth="4xl"
    >
      <form
        onSubmit={handleSubmit(handleSubmitForm)}
        data-test-id="finished-goods-production-form"
        className="space-y-6"
      >
        {/* Marker so overlay configs can identify the active modal mode. Uses
            sr-only (not `hidden`) because the onboarding overlay detects it via
            isElementVisible, which rejects display:none elements — an sr-only
            span is laid out (1px) yet invisible, so it stays detectable.
            `data-onboarding-sample` narrows the draft-completion phases to the
            throwaway draft the tour itself created, so opening a REAL draft mid-
            tour never gets guided as if it were practice. */}
        <span
          data-test-id={`fg-production-mode-${mode}`}
          data-onboarding-sample={isOnboardingSampleProduction ? 'true' : undefined}
          className="sr-only"
          aria-hidden
        />
        {loading && (
          <div className="flex items-center justify-center py-6" data-test-id="finished-goods-loading">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-600" />
          </div>
        )}
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700" data-test-id="finished-goods-error">
            {error}
          </div>
        )}

        <FinishedGoodsFinalProductSection
          control={control}
          register={register}
          errors={errors}
          isSubmitted={isSubmitted}
          unit={unit}
          toggleUnit={toggleUnit}
          finishedGoodsOptions={finishedGoodsOptions}
          finalProductMode={finalProductMode}
          readOnly={readOnly || mode === 'complete_draft'}
          showValidation={showDraftValidation && mode === 'create_draft'}
          isFinalProductMissing={showDraftValidation && mode === 'create_draft' ? missingFinalProduct : false}
          isFinalProductDuplicate={mode === 'create_draft' ? isDuplicateFinalProductName : false}
          isPackSizeMissing={showDraftValidation && mode === 'create_draft' ? missingPackSize : false}
          disableFinalProductSelect={mode !== 'create_draft' || readOnly}
          showPackSize={true}
          showLocation={true}
          onSwitchToNew={handleModeSwitchAndPrefill}
        />

        <div
          className={`space-y-3 ${
            mode === 'create_draft' && showDraftValidation && (missingComponents || invalidComponents || invalidTotals)
              ? 'rounded-lg border border-red-200 bg-red-50/30 p-2'
              : ''
          }`}
          data-test-id="finished-goods-components-wrapper"
        >
          {!readOnly && (
            <div
              className="flex items-start gap-2.5 rounded-md border-l-2 border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600"
              data-test-id="finished-goods-wo-only-notice"
            >
              <svg className="mt-0.5 h-3.5 w-3.5 flex-none text-slate-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span>{t('finishedGoodsProduction.modal.workOrderOnlyNotice')}</span>
            </div>
          )}
          <FinishedGoodsComponentsSection
            control={control}
            errors={errors}
            fields={fields}
            components={components}
            unit={unit}
            finalProductMode={finalProductMode}
            packSizeKg={packSizeKg}
            finalQuantityPcs={finalQuantityPcs}
            totalGrams={totalGrams}
            componentQuantityInputs={componentQuantityInputs}
            handleAddComponent={handleAddComponent}
            handleComponentSelectChange={handleComponentSelectChange}
            availableOptionsForRow={availableOptionsForRow}
            getRoastedBulkLabel={getRoastedBulkLabel}
            handlePercentageChange={handlePercentageChange}
            handleComponentWeightChange={handleComponentWeightChange}
            handleFinalComponentWeightChange={handleFinalComponentWeightChange}
            remove={remove}
            readOnly={readOnly}
            showFinalAllocation={mode === 'complete_draft'}
          />
          {mode === 'create_draft' && showDraftValidation && missingComponents && (
            <div className="px-1 text-xs font-medium text-red-700" data-test-id="finished-goods-components-required">
              {t('finishedGoodsProduction.errors.componentRequired')}
            </div>
          )}
          {mode === 'create_draft' && showDraftValidation && !missingComponents && (invalidComponents || invalidTotals) && (
            <div className="px-1 text-xs font-medium text-red-700" data-test-id="finished-goods-components-invalid">
              {t('finishedGoodsProduction.errors.allocationInvalid')}
            </div>
          )}
          <div className="flex flex-col md:flex-row md:items-start gap-3 rounded-md border border-gray-200 p-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">{t('finishedGoodsProduction.modal.finalQuantityPcs')}</label>
              <input
                type="number"
                min={0}
                step={1}
                data-test-id="finished-goods-final-quantity"
                disabled={readOnly}
                value={finalQuantityInput}
                onChange={event => {
                  if (isCancelledView) return;
                  const raw = event.target.value;
                  setFinalQuantityInput(raw);
                  if (raw === '') {
                    setValue('finalQuantityPcs', 0, { shouldValidate: true });
                    return;
                  }
                  const nextValue = parseInt(raw, 10);
                  setValue('finalQuantityPcs', Number.isNaN(nextValue) ? 0 : nextValue, { shouldValidate: true });
                }}
                className={`mt-1 block w-full p-2 rounded-md border shadow-sm sm:text-sm ${
                  mode === 'create_draft' && showDraftValidation && missingFinalQuantity
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              <div className="mt-1 min-h-4">
                {maxQuantity > 0 && finalQuantityPcs > maxQuantity && (
                  <p className="text-xs text-red-600" data-test-id="finished-goods-qty-warning">
                    {t('finishedGoodsProduction.errors.exceedsMaxQty', { maxQuantity })}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-none md:pt-6">
              <button
                type="button"
                onClick={handleCalculateMaxQuantity}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                data-test-id="finished-goods-calc-max"
                disabled={readOnly}
              >
                {t('finishedGoodsProduction.modal.calculateMaxQty')}
              </button>
            </div>
          </div>

          <FinishedGoodsSummarySection
            control={control}
            miscCostFields={miscCostFields}
            onAddMiscCost={handleAddMiscCost}
            onRemoveMiscCost={removeMiscCost}
            totalInputKg={totalInputKg}
            finalQuantityPcs={finalQuantityPcs}
            totalInputCost={totalInputCost}
            unitCostPerPiece={unitCostPerPiece}
            estimatedSellingPrice={estimatedSellingPrice}
            onEstimatedSellingPriceChange={(val) => setValue('estimatedSellingPrice', val, { shouldValidate: true })}
            readOnly={readOnly}
          />
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          {showDraftBlockingMessage && (
            <div className="mr-auto rounded-md bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900" data-test-id="finished-goods-draft-blocked">
              {t('finishedGoodsProduction.modal.completeRequiredToSave')}
            </div>
          )}
          <button
            type="button"
            data-test-id="finished-goods-cancel"
            onClick={requestClose}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            {readOnly ? t('common.actions.close') : t('auth.common.cancel')}
          </button>
          {!readOnly && (
            <button
              type="submit"
              data-test-id="finished-goods-confirm"
              disabled={disableSubmit}
              className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? t('finishedGoodsProduction.modal.processing') : mode === 'complete_draft' ? t('finishedGoodsProduction.modal.complete') : t('forms.actions.save')}
            </button>
          )}
        </div>
      </form>
    </Modal>

    <ConfirmationDialog
      isOpen={isCloseConfirmOpen}
      onClose={() => setIsCloseConfirmOpen(false)}
      onConfirm={confirmClose}
      title={t('finishedGoodsProduction.modal.closeConfirmTitle')}
      message={t('finishedGoodsProduction.modal.closeConfirmMessage')}
      confirmLabel={t('finishedGoodsProduction.modal.discardChanges')}
      cancelLabel={t('finishedGoodsProduction.modal.keepEditing')}
      isDestructive
    />
    </>
  );
};
